import React, { useRef, useState, useContext } from "react";
import AdminFooter from "../components/commonComponents/AdminFooter";
import { TextField, Button } from "@mui/material";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Modal from "react-modal";
import "./register.scss";
import PasswordValidator from "password-validator";
import { AuthContext } from "../context/AuthContext";
import { GeneralContext } from "../context/GeneralContext";
import { api } from "../utils/authActions";
import { useNavigate } from "react-router-dom";
import { hasProperty, validateEmail } from "../utils/helpers";
import PasswordEntry from "../components/adminComponents/PasswordEntry";
import toast from "react-hot-toast";
import PrivacyPage from "./legal/PrivacyPage";
import TermsOfUse from "./legal/TermsOfUse";

function Register() {
  const { authToken } = useContext(AuthContext);
  const { recordedErrorLog } = useContext(GeneralContext);
  const navigate = useNavigate();

  // Attach ref to page root so react-modal has a stable appElement
  const appRef = useRef(null);

  // Modals
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  // Consents on the form (match original layout)
  const [acceptLegal, setAcceptLegal] = useState(false);     // agree to Privacy + Terms
  const [consentedNews, setConsentedNews] = useState(false); // marketing/newsletters

  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [email, setEmail]         = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [emailMissmatch, setEmailMissmatch] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [password, setPassword] = useState("");
  const [affiliation, setAffiliation] = useState("");
  const [country, setCountry] = useState("");
  const [emailAlreadyUsed, setEmailAlreadyUsed] = useState(false);

  // “How did you find us?” single-select
  const [questions, setQuestions] = useState({
    "From a colleague": false,
    "Via ResearchGate": false,
    "Via a search engine": false,
    "From (scientific) publications": false,
  });

  // Background single-select
  const [background, setBackground] = useState({
    "Mostly academia": false,
    "Mostly industry": false,
    "Mostly education": false,
  });

  // Password policy
  const schema = new PasswordValidator();
  schema
    .is().min(8)
    .is().max(100)
    .has().uppercase()
    .has().lowercase()
    .has().digits(2)
    .has().not().spaces()
    .is().not().oneOf(["Passw0rd", "Password123"]);

  const handleQuestionsChange = (event) => {
    setQuestions((old) => {
      const newState = Object.keys(old).reduce((acc, k) => ({ ...acc, [k]: false }), {});
      newState[event.target.name] = event.target.checked;
      return newState;
    });
  };

  const handleBackgroundChange = (event) => {
    setBackground((old) => {
      const newState = Object.keys(old).reduce((acc, k) => ({ ...acc, [k]: false }), {});
      newState[event.target.name] = event.target.checked;
      return newState;
    });
  };

  const checkEmailMatch = () => {
    setEmailMissmatch(emailConfirm !== "" && email !== emailConfirm);
  };

  const handleEmailBlur = () => {
    setEmailValid(!(email !== "" && !validateEmail(email)));
    if (emailConfirm !== "") checkEmailMatch();
  };

  const handleClosePrivacyModal = () => setPrivacyOpen(false);
  const handleCloseTermsModal   = () => setTermsOpen(false);

  const checkIfAllowedToClick = () => {
    return (
      password !== "" &&
      schema.validate(password) &&
      email !== "" &&
      email === emailConfirm &&
      validateEmail(email) &&
      firstName !== "" &&
      lastName !== "" &&
      affiliation !== "" &&
      country !== "" &&
      Object.values(questions).some((v) => v) &&
      Object.values(background).some((v) => v)
      // Note: we gate the Register button visibility by acceptLegal (see below)
    );
  };

  const trueKeysToString = (state) =>
    Object.entries(state)
      .filter(([, value]) => value)
      .map(([key]) => key)
      .join("; ");

  const handleRegister = () => {
    if (!checkIfAllowedToClick() || !acceptLegal) return;

    const payload = {
      email,
      password,
      full_name: `${firstName} ${lastName}`,
      affiliation,
      country,
      found_us: trueKeysToString(questions),
      background: trueKeysToString(background),
      newsletter: consentedNews,
    };

    api
      .createUser(authToken, payload)
      .then(() => navigate("/welcome")) // Your plan selection lives there
      .catch((err) => {
        if (
          hasProperty(err, "response") &&
          hasProperty(err.response, "status") &&
          err.response.status === 409
        ) {
          const errorMessage =
            hasProperty(err.response, "data") &&
            hasProperty(err.response.data, "detail")
              ? err.response.data.detail
              : "Server error occurred";
          toast.error(errorMessage);
          if (errorMessage === "An account with this email address already exists.") {
            setEmailAlreadyUsed(true);
          }
        } else {
          const errorDetails = hasProperty(err, "response")
            ? `Backend response: ${JSON.stringify(err.response, null, 2)}`
            : "No backend response available";
          recordedErrorLog(
            `THERE WAS AN ERROR WITH USER CREATION: ${errorDetails}`,
            err,
            true,
            "Unexpected error occurred while creating user. Please try again or contact support."
          );
        }
      });
  };

  return (
    <div className="registerPage" ref={appRef}>
      <div className="pageContentContainer">
        <h1 className="title">Create your Speqqle account</h1>

        <div className="detailEntryContainer">
          {/* NAME SECTION */}
          <div className="entrySection">
            <div className="sectionTitle">Name*</div>
            <div className="entries">
              <div className="entry">
                <TextField
                  className="entryField"
                  size="small"
                  label={"First"}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="entry">
                <TextField
                  className="entryField"
                  label={"Last"}
                  size="small"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* EMAIL SECTION */}
          <div className="entrySection">
            <div className="sectionTitle">Email*</div>
            <div className="entries">
              <div className="entry">
                <TextField
                  className="entryField"
                  size="small"
                  label={"Email"}
                  value={email}
                  onChange={(e) => {
                    setEmailValid(true);
                    setEmail(e.target.value);
                  }}
                  onBlur={handleEmailBlur}
                  error={emailAlreadyUsed}
                />
              </div>
              <div className="entry">
                <TextField
                  className="entryField"
                  size="small"
                  label={"Confirm Email"}
                  value={emailConfirm}
                  onChange={(e) => {
                    setEmailValid(true);
                    setEmailConfirm(e.target.value);
                  }}
                  onBlur={checkEmailMatch}
                  error={emailAlreadyUsed}
                />
              </div>
            </div>
            {emailAlreadyUsed ? (
              <div className="emailInUse">Email is already in use.</div>
            ) : null}
          </div>
          {emailMissmatch ? <div className="missmatch">Emails do not match!</div> : null}
          {!emailValid ? <div className="missmatch">Email is not valid!</div> : null}

          {/* PASSWORD SECTION */}
          <PasswordEntry title={"Password*"} setFinalPass={setPassword} />
        </div>

        {/* QUESTIONS AREA */}
        <div className="questionEntryContainer">
          <div className="questionSection">
            <div className="question">How did you find out about Speqqle?*</div>
            <div className="replyArea">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questions["From a colleague"]}
                      onChange={handleQuestionsChange}
                      name="From a colleague"
                    />
                  }
                  label="From a colleague"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questions["Via ResearchGate"]}
                      onChange={handleQuestionsChange}
                      name="Via ResearchGate"
                    />
                  }
                  label="Via ResearchGate"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questions["Via a search engine"]}
                      onChange={handleQuestionsChange}
                      name="Via a search engine"
                    />
                  }
                  label="Via a search engine"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questions["From (scientific) publications"]}
                      onChange={handleQuestionsChange}
                      name="From (scientific) publications"
                    />
                  }
                  label="From (scientific) publications"
                />
              </FormGroup>
            </div>
          </div>

          <div className="questionSection">
            <div className="question">What is your background?*</div>
            <div className="replyArea">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={background["Mostly academia"]}
                      onChange={handleBackgroundChange}
                      name="Mostly academia"
                    />
                  }
                  label="Mostly academia"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={background["Mostly industry"]}
                      onChange={handleBackgroundChange}
                      name="Mostly industry"
                    />
                  }
                  label="Mostly industry"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={background["Mostly education"]}
                      onChange={handleBackgroundChange}
                      name="Mostly education"
                    />
                  }
                  label="Mostly education"
                />
              </FormGroup>
            </div>
          </div>

          <div className="questionSection">
            <div className="question">Company/University Name*</div>
            <div className="entries">
              <div className="entry">
                <TextField
                  className="entryField"
                  size="small"
                  label={"Company/University Name"}
                  value={affiliation}
                  onChange={(e) => setAffiliation(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="questionSection">
            <div className="question">Country*</div>
            <div className="entries">
              <div className="entry">
                <TextField
                  className="entryField"
                  size="small"
                  label={"Country"}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* CONSENT AREA (same structure/classes as original) */}
          <div className="questionSection">
            <div className="question">Consent</div>
            <div className="replyArea">
              <FormGroup>
                <FormControlLabel
                  required
                  control={
                    <Checkbox
                      checked={acceptLegal}
                      onChange={(e) => setAcceptLegal(e.target.checked)}
                    />
                  }
                  label={
                    <span>
                      I agree to the{" "}
                      <span className="policyLink" onClick={() => setTermsOpen(true)} style={{ color: '#1976d2', cursor: 'pointer' }}>
                        Terms of Use
                      </span>{" "}
                      and the{" "}
                      <span className="policyLink" onClick={() => setPrivacyOpen(true)} style={{ color: '#1976d2', cursor: 'pointer' }}>
                        Privacy Policy
                      </span>
                      .
                    </span>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={consentedNews}
                      onChange={(e) => setConsentedNews(e.target.checked)}
                    />
                  }
                  label="I agree to receive newsletters"
                />
              </FormGroup>

              {/* Keep original link blocks & class for styling */}
              <div className="privacyPolicy" onClick={() => setPrivacyOpen(true)}>
                Privacy Policy
              </div>
              <div className="privacyPolicy" onClick={() => setTermsOpen(true)}>
                Terms of Use
              </div>
            </div>
          </div>

          {/* Show the action area only when legal is accepted (like your original) */}
          {acceptLegal ? (
            <div className="afterConsent">
              {/* <div className="captchaArea">Here is supposed to be captcha</div> */}
              <div className="registerButtonArea">
                <Button
                  disabled={!checkIfAllowedToClick()}
                  variant="contained"
                  onClick={handleRegister}
                  className="registerButton"
                >
                  Register
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* PRIVACY MODAL */}
      <Modal
        isOpen={privacyOpen}
        onRequestClose={handleClosePrivacyModal}
        shouldCloseOnOverlayClick={true}
        contentLabel="Privacy Modal"
        appElement={appRef.current}
        style={{
          content: {
            top: "35%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -35%)",
            minWidth: "440px",
            width: "80vw",
            minHeight: "650px",
            height: "70vh",
          },
          overlay: { zIndex: "100" },
        }}
      >
        <PrivacyPage />
      </Modal>

      {/* TERMS MODAL */}
      <Modal
        isOpen={termsOpen}
        onRequestClose={handleCloseTermsModal}
        shouldCloseOnOverlayClick={true}
        contentLabel="Terms Modal"
        appElement={appRef.current}
        style={{
          content: {
            top: "35%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -35%)",
            minWidth: "440px",
            width: "80vw",
            minHeight: "650px",
            height: "70vh",
          },
          overlay: { zIndex: "100" },
        }}
      >
        <TermsOfUse />
      </Modal>

      <AdminFooter />
    </div>
  );
}

export default Register;
